import type { CursorPage, ValidationResult } from "@led-signage/shared/common";
import type { DeviceConnectivity } from "@led-signage/shared/device-api";
import type {
  AuditLogRecord,
  DisplayDetail,
  MediaAsset,
  PlaybackEventRecord,
  Playlist,
  PlaylistRevision,
  PublicationRequest,
  Schedule,
  ScheduleDetail,
  ScheduleRevision,
  DeviceReleaseProgress,
} from "@led-signage/shared/management-api";
import { ApiError, type ManagementApi } from "./client.js";

/**
 * 実APIが未デプロイの段階で画面を確認するための応答。
 * 契約型をそのまま使うため、`shared/` が変われば型検査で落ちる。
 * 実在の顧客情報や配信内容は含めない。
 */

function page<T>(items: readonly T[]): CursorPage<T> {
  return { items, nextCursor: null };
}

const ORG = "org_demo";

const media: readonly MediaAsset[] = [
  {
    id: "med_summer",
    organizationId: ORG,
    originalName: "summer_sale_v2.mp4",
    contentType: "video/mp4",
    byteSize: 73_400_320,
    sha256: "a".repeat(64),
    status: "ready",
    durationMs: 30_000,
    videoCodec: "avc1",
    audioCodec: null,
    width: 1920,
    height: 1080,
    frameRateMilliHz: 30_000,
    hasAudio: false,
    validationError: null,
    createdBy: "usr_editor",
    createdAt: "2026-08-20T02:10:00.000Z",
    validatedAt: "2026-08-20T02:12:00.000Z",
    archivedAt: null,
  },
  {
    id: "med_brand",
    organizationId: ORG,
    originalName: "brand_movie_2026.mp4",
    contentType: "video/mp4",
    byteSize: 146_800_640,
    sha256: "b".repeat(64),
    status: "ready",
    durationMs: 60_000,
    videoCodec: "avc1",
    audioCodec: "mp4a",
    width: 1920,
    height: 1080,
    frameRateMilliHz: 30_000,
    hasAudio: true,
    validationError: null,
    createdBy: "usr_editor",
    createdAt: "2026-08-19T08:00:00.000Z",
    validatedAt: "2026-08-19T08:03:00.000Z",
    archivedAt: null,
  },
  {
    id: "med_weekend",
    organizationId: ORG,
    originalName: "weekend_event.mp4",
    contentType: "video/mp4",
    byteSize: 61_200_000,
    sha256: "c".repeat(64),
    status: "validating",
    durationMs: null,
    videoCodec: null,
    audioCodec: null,
    width: null,
    height: null,
    frameRateMilliHz: null,
    hasAudio: false,
    validationError: null,
    createdBy: "usr_editor",
    createdAt: "2026-08-21T01:40:00.000Z",
    validatedAt: null,
    archivedAt: null,
  },
  {
    id: "med_old",
    organizationId: ORG,
    originalName: "old_campaign.mp4",
    contentType: "video/mp4",
    byteSize: 41_000_000,
    sha256: "d".repeat(64),
    status: "rejected",
    durationMs: 45_000,
    videoCodec: "hvc1",
    audioCodec: null,
    width: 1280,
    height: 720,
    frameRateMilliHz: 30_000,
    hasAudio: false,
    validationError: "FORMAT_UNSUPPORTED: 映像コーデックがH.264ではありません",
    createdBy: "usr_editor",
    createdAt: "2026-07-30T05:00:00.000Z",
    validatedAt: "2026-07-30T05:02:00.000Z",
    archivedAt: null,
  },
];

function display(
  id: string,
  name: string,
  connectivity: DeviceConnectivity,
  options: {
    playbackState: "playing" | "fallback" | "stopped";
    currentMediaAssetId: string | null;
    freeStorageBytes: number;
    clockOffsetMs: number;
    lastSeenAt: string;
    currentReleaseId: string | null;
    desiredReleaseId: string | null;
    eventQueueDepth?: number;
    eventQueueDroppedCount?: number;
  },
): DisplayDetail {
  return {
    display: {
      etag: `"1"`,
      id,
      organizationId: ORG,
      siteId: null,
      name,
      timezone: "Asia/Tokyo",
      outputWidth: 1920,
      outputHeight: 1080,
      refreshRateMilliHz: 30_000,
      orientation: "landscape",
      defaultAssetId: "med_summer",
      createdAt: "2026-07-01T00:00:00.000Z",
      updatedAt: "2026-08-20T00:00:00.000Z",
      archivedAt: null,
    },
    device: {
      id: `dev_${id}`,
      displayId: id,
      name: `${name} 再生端末`,
      platform: "windows",
      agentVersion: "0.1.0",
      enrolledAt: "2026-07-02T00:00:00.000Z",
      createdAt: "2026-07-01T00:00:00.000Z",
      retiredAt: null,
    },
    deviceStatus: {
      connectivity,
      currentReleaseId: options.currentReleaseId,
      desiredReleaseId: options.desiredReleaseId,
      playbackState: options.playbackState,
      currentMediaAssetId: options.currentMediaAssetId,
      positionMs: 12_400,
      freeStorageBytes: options.freeStorageBytes,
      clockOffsetMs: options.clockOffsetMs,
      lastErrorCode: null,
      lastErrorMessage: null,
      lastSeenAt: options.lastSeenAt,
      eventQueueDepth: options.eventQueueDepth ?? 0,
      eventQueueDroppedCount: options.eventQueueDroppedCount ?? 0,
    },
    desiredRelease: null,
  };
}

const displays: readonly DisplayDetail[] = [
  display("dsp_naha", "那覇メインLED", "online", {
    playbackState: "playing",
    currentMediaAssetId: "med_summer",
    freeStorageBytes: 96_000_000_000,
    clockOffsetMs: 120,
    lastSeenAt: "2026-08-21T02:59:40.000Z",
    currentReleaseId: "rel_42",
    desiredReleaseId: "rel_42",
  }),
  display("dsp_kokusai", "国際通りLED", "online", {
    playbackState: "playing",
    currentMediaAssetId: "med_brand",
    freeStorageBytes: 88_000_000_000,
    clockOffsetMs: -400,
    lastSeenAt: "2026-08-21T02:59:12.000Z",
    currentReleaseId: "rel_42",
    desiredReleaseId: "rel_42",
  }),
  // 希望版と現在版が異なる = 配信中
  display("dsp_chatan", "北谷イベントLED", "online", {
    playbackState: "playing",
    currentMediaAssetId: "med_summer",
    freeStorageBytes: 12_000_000_000,
    clockOffsetMs: 900,
    lastSeenAt: "2026-08-21T02:58:55.000Z",
    currentReleaseId: "rel_41",
    desiredReleaseId: "rel_43",
  }),
  // clock offset が閾値超え + オフライン
  display("dsp_lab", "検証用ディスプレイ", "offline", {
    playbackState: "fallback",
    currentMediaAssetId: null,
    freeStorageBytes: 4_000_000_000,
    clockOffsetMs: 48_000,
    lastSeenAt: "2026-08-21T02:47:03.000Z",
    currentReleaseId: "rel_40",
    desiredReleaseId: "rel_43",
  }),
];

function initialPlaylists(): Playlist[] {
  return [
    {
      id: "pl_summer",
      organizationId: ORG,
      name: "夏キャンペーン",
      description: "正面LED用の夏季素材",
      createdAt: "2026-08-18T01:00:00.000Z",
      updatedAt: "2026-08-21T01:00:00.000Z",
      archivedAt: null,
      etag: '"4"',
    },
    {
      id: "pl_regular",
      organizationId: ORG,
      name: "通常営業ループ",
      description: "平日の通常放映",
      createdAt: "2026-08-01T01:00:00.000Z",
      updatedAt: "2026-08-20T01:00:00.000Z",
      archivedAt: null,
      etag: '"2"',
    },
  ];
}

function initialPlaylistRevisions(): PlaylistRevision[] {
  return [
    {
      id: "plr_summer_4",
      playlistId: "pl_summer",
      revisionNumber: 4,
      state: "draft",
      defaultAudioEnabled: false,
      items: [
        { position: 0, mediaAssetId: "med_summer", repeatCount: 1, audioEnabled: null },
        { position: 1, mediaAssetId: "med_brand", repeatCount: 1, audioEnabled: false },
      ],
      totalDurationMs: 90_000,
      createdBy: "usr_editor",
      createdAt: "2026-08-21T01:00:00.000Z",
      updatedAt: "2026-08-21T01:00:00.000Z",
      approvedAt: null,
      etag: '"1"',
    },
    {
      id: "plr_regular_1",
      playlistId: "pl_regular",
      revisionNumber: 1,
      state: "approved",
      defaultAudioEnabled: false,
      items: [
        { position: 0, mediaAssetId: "med_summer", repeatCount: 3, audioEnabled: null },
      ],
      totalDurationMs: 90_000,
      createdBy: "usr_editor",
      createdAt: "2026-08-10T01:00:00.000Z",
      updatedAt: "2026-08-10T01:00:00.000Z",
      approvedAt: "2026-08-10T02:00:00.000Z",
      etag: '"1"',
    },
  ];
}

// 承認・差し戻しで状態が変わる。インスタンスごとに独立させるため関数で作る。
function initialPublicationRequests(): PublicationRequest[] {
  return [
  {
    id: "pub_summer",
    organizationId: ORG,
    status: "submitted",
    requestedBy: "usr_editor",
    scheduleRevisionIds: ["scr_summer"],
    displayIds: ["dsp_naha", "dsp_kokusai"],
    submittedAt: "2026-08-21T01:20:00.000Z",
    reviewedBy: null,
    reviewedAt: null,
    reviewComment: null,
    changeReason: "夏休みセールの第2版へ差し替え",
    sameDayChange: false,
    emergencyOverride: false,
    emergencyEndsAt: null,
    createdAt: "2026-08-21T01:00:00.000Z",
    updatedAt: "2026-08-21T01:20:00.000Z",
    validation: {
      valid: true,
      errors: [],
      warnings: [
        {
          code: "AUDIO_ENABLED",
          message: "音声を有効にした項目があります。放映前に音量を確認してください。",
        },
      ],
    },
  },
  {
    id: "pub_weekend",
    organizationId: ORG,
    status: "submitted",
    requestedBy: "usr_editor2",
    scheduleRevisionIds: ["scr_weekend"],
    displayIds: ["dsp_chatan"],
    submittedAt: "2026-08-21T02:40:00.000Z",
    reviewedBy: null,
    reviewedAt: null,
    reviewComment: null,
    changeReason: "週末イベント告知の当日追加",
    sameDayChange: true,
    emergencyOverride: false,
    emergencyEndsAt: null,
    createdAt: "2026-08-21T02:35:00.000Z",
    updatedAt: "2026-08-21T02:40:00.000Z",
    validation: {
      valid: false,
      errors: [
        {
          code: "MEDIA_NOT_READY",
          message: "weekend_event.mp4 の形式検査が完了していません。",
          resourceId: "med_weekend",
        },
      ],
      warnings: [],
    },
  },
  ];
}

const auditLogs: readonly AuditLogRecord[] = [
  {
    id: "aud_1",
    actorType: "user",
    actorId: "usr_editor",
    actorName: "山田 花子",
    action: "media.upload.complete",
    targetType: "media_asset",
    targetId: "med_brand",
    displayId: null,
    reason: null,
    before: { status: "uploading" },
    after: { status: "uploaded" },
    requestId: "req_1",
    outcome: "success",
    errorCode: null,
    errorMessage: null,
    occurredAt: "2026-08-21T02:48:00.000Z",
  },
  {
    id: "aud_2",
    actorType: "user",
    actorId: "usr_approver",
    actorName: "佐藤 太郎",
    action: "publication_request.approve",
    targetType: "publication_request",
    targetId: "pub_prev",
    displayId: "dsp_naha",
    reason: null,
    before: { status: "submitted" },
    after: { status: "approved" },
    requestId: "req_2",
    outcome: "success",
    errorCode: null,
    errorMessage: null,
    occurredAt: "2026-08-21T02:22:00.000Z",
  },
  {
    id: "aud_3",
    actorType: "system",
    actorId: null,
    actorName: null,
    action: "release.activate",
    targetType: "release",
    targetId: "rel_42",
    displayId: "dsp_naha",
    reason: null,
    before: null,
    after: { status: "active" },
    requestId: "req_3",
    outcome: "success",
    errorCode: null,
    errorMessage: null,
    occurredAt: "2026-08-21T02:18:00.000Z",
  },
  {
    id: "aud_4",
    actorType: "user",
    actorId: "usr_viewer",
    actorName: "現地 保守",
    action: "authorization.denied",
    targetType: "permission",
    targetId: "publication:publish",
    displayId: null,
    reason: null,
    before: null,
    after: null,
    requestId: "req_4",
    outcome: "failure",
    errorCode: "PERMISSION_DENIED",
    errorMessage: "現在のロールではこの操作を実行できません。",
    occurredAt: "2026-08-21T02:05:00.000Z",
  },
];

const playbackEvents: readonly PlaybackEventRecord[] = [
  {
    id: "evt_play_5",
    deviceId: "dev_dsp_lab",
    releaseId: "rel_40",
    mediaAssetId: null,
    eventType: "fallback_started",
    occurredAt: "2026-08-21T02:47:03.000Z",
    positionMs: null,
    detail: { reason: "network_unavailable" },
  },
  {
    id: "evt_play_4",
    deviceId: "dev_dsp_chatan",
    releaseId: "rel_41",
    mediaAssetId: "med_summer",
    eventType: "play_interrupted",
    occurredAt: "2026-08-21T02:42:10.000Z",
    positionMs: 18_420,
    detail: { reason: "release_changed" },
  },
  {
    id: "evt_play_3",
    deviceId: "dev_dsp_kokusai",
    releaseId: "rel_42",
    mediaAssetId: "med_brand",
    eventType: "play_completed",
    occurredAt: "2026-08-21T02:41:00.000Z",
    positionMs: 60_000,
    detail: null,
  },
  {
    id: "evt_play_2",
    deviceId: "dev_dsp_kokusai",
    releaseId: "rel_42",
    mediaAssetId: "med_brand",
    eventType: "play_started",
    occurredAt: "2026-08-21T02:40:00.000Z",
    positionMs: 0,
    detail: null,
  },
  {
    id: "evt_play_1",
    deviceId: "dev_dsp_naha",
    releaseId: "rel_42",
    mediaAssetId: "med_summer",
    eventType: "play_failed",
    occurredAt: "2026-08-21T02:39:30.000Z",
    positionMs: 4_800,
    detail: { errorCode: "MPV_EXITED" },
  },
];

/** fixtureでの承認者。自己承認の拒否を再現するために使う */

function initialSchedules(): Schedule[] {
  return [
    {
      id: "sch_summer",
      organizationId: ORG,
      name: "サマーキャンペーン編成",
      description: null,
      createdAt: "2026-08-20T01:00:00.000Z",
      updatedAt: "2026-08-21T02:00:00.000Z",
      archivedAt: null,
      etag: '"2"',
    },
  ];
}

function initialScheduleRevisions(): ScheduleRevision[] {
  return [
    {
      id: "scr_summer_1",
      scheduleId: "sch_summer",
      revisionNumber: 1,
      playlistRevisionId: "plr_summer_4",
      state: "draft",
      validFrom: "2026-08-25",
      validUntil: "2026-09-30",
      startTime: "09:00:00",
      endTime: "21:00:00",
      spansMidnight: false,
      daysMask: 0b1111111,
      priority: 400,
      transitionMode: "finish_current",
      maxWaitSeconds: null,
      displayIds: ["dsp_naha_main"],
      createdBy: "usr_editor",
      createdAt: "2026-08-20T01:00:00.000Z",
      updatedAt: "2026-08-21T02:00:00.000Z",
      approvedAt: null,
      etag: '"2"',
    },
  ];
}

const FIXTURE_ACTOR = "usr_approver";

/** 画面確認用の代替画像。実物のスクリーンショットは署名付きURLで取得する。 */
const FIXTURE_SCREENSHOT =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360">' +
      '<rect width="640" height="360" fill="#0b1020"/>' +
      '<text x="320" y="188" fill="#9db2ff" font-family="sans-serif" ' +
      'font-size="28" text-anchor="middle">fixture screenshot</text></svg>',
  );

/**
 * 取得中と適用済みが混ざった状態を返す。
 *
 * 「全部そろっている」画面だけ見て作ると、途中経過の表示が抜ける。
 */
function deviceReleaseProgress(): readonly DeviceReleaseProgress[] {
  return [
    {
      deviceId: "dev_fixture",
      releaseId: "rel_0042",
      status: "downloading",
      bytesDownloaded: 41_237_299,
      errorCode: null,
      errorMessage: null,
      updatedAt: "2026-08-29T03:59:30.000Z",
      readyAt: null,
      activatedAt: null,
    },
    {
      deviceId: "dev_fixture",
      releaseId: "rel_0041",
      status: "active",
      bytesDownloaded: 82_188_004,
      errorCode: null,
      errorMessage: null,
      updatedAt: "2026-08-29T01:12:00.000Z",
      readyAt: "2026-08-29T01:10:44.000Z",
      activatedAt: "2026-08-29T01:12:00.000Z",
    },
    {
      deviceId: "dev_fixture",
      releaseId: "rel_0040",
      status: "failed",
      bytesDownloaded: 3_120_884,
      errorCode: "ASSET_HASH_MISMATCH",
      errorMessage: "動画の照合値が一致しませんでした。",
      updatedAt: "2026-08-28T22:40:10.000Z",
      readyAt: null,
      activatedAt: null,
    },
  ];
}

/** fixtureでは書き込みを再現しない。実APIでのみ実行できることを明示する。 */
function unavailable(operation: string): ApiError {
  return new ApiError(
    503,
    "SERVICE_UNAVAILABLE",
    `${operation}はローカル確認用の表示では実行できません。`,
    null,
  );
}

export function createFixtureApi(): ManagementApi {
  const publicationRequests = initialPublicationRequests();
  const playlists = initialPlaylists();
  const playlistRevisions = initialPlaylistRevisions();
  const schedules = initialSchedules();
  const scheduleRevisions = initialScheduleRevisions();
  const delay = <T>(value: T): Promise<T> =>
    new Promise((resolve) => setTimeout(() => { resolve(value); }, 120));

  return {
    listMedia: (query) =>
      delay(
        page(
          query?.status === undefined
            ? media
            : media.filter((item) => item.status === query.status),
        ),
      ),
    createMediaUploadIntent: () =>
      Promise.reject(
        new Error("アップロードはfixtureでは実行できません。実APIへ接続してください。"),
      ),
    completeMediaUpload: () =>
      Promise.reject(
        new Error("アップロードはfixtureでは実行できません。実APIへ接続してください。"),
      ),
    archiveMedia: () => Promise.reject(unavailable("動画のアーカイブ")),
    getMediaPreview: (mediaId) => {
      const found = media.find((item) => item.id === mediaId);
      return found === undefined
        ? Promise.reject(
            new ApiError(404, "NOT_FOUND", "動画が見つかりません。", null),
          )
        : delay({
            media: found,
            // fixtureでは実体が無い。再生はできないが導線は確認できる。
            downloadUrl: "data:video/mp4;base64,",
            downloadUrlExpiresAt: "2026-08-29T04:05:00.000Z",
          });
    },
    listPlaylists: () => delay(page(playlists)),
    createPlaylist: (input) => {
      if (playlists.some((item) => item.name === input.name)) {
        return Promise.reject(
          new ApiError(409, "VALIDATION_FAILED", "同じ名前のプレイリストがあります。", null),
        );
      }
      const now = new Date().toISOString();
      const created: Playlist = {
        id: `pl_${crypto.randomUUID()}`,
        organizationId: ORG,
        name: input.name,
        description: input.description ?? null,
        createdAt: now,
        updatedAt: now,
        archivedAt: null,
        etag: '"1"',
      };
      playlists.unshift(created);
      return delay(created);
    },
    getPlaylist: (id) => {
      const playlist = playlists.find((item) => item.id === id);
      if (playlist === undefined) {
        return Promise.reject(new ApiError(404, "NOT_FOUND", "プレイリストが見つかりません。", null));
      }
      const revisions = playlistRevisions
        .filter((revision) => revision.playlistId === id)
        .sort((left, right) => right.revisionNumber - left.revisionNumber);
      return delay({ playlist, latestRevision: revisions[0] ?? null, revisions });
    },
    createPlaylistRevision: (playlistId, input) => {
      const playlist = playlists.find((item) => item.id === playlistId);
      if (playlist === undefined) {
        return Promise.reject(new ApiError(404, "NOT_FOUND", "プレイリストが見つかりません。", null));
      }
      const revisions = playlistRevisions.filter((item) => item.playlistId === playlistId);
      const source = input.sourceRevisionId === undefined
        ? undefined
        : revisions.find((item) => item.id === input.sourceRevisionId);
      if (input.sourceRevisionId !== undefined && source === undefined) {
        return Promise.reject(new ApiError(400, "VALIDATION_FAILED", "複製元の版が見つかりません。", null));
      }
      const now = new Date().toISOString();
      const created: PlaylistRevision = {
        id: `plr_${crypto.randomUUID()}`,
        playlistId,
        revisionNumber: Math.max(0, ...revisions.map((item) => item.revisionNumber)) + 1,
        state: "draft",
        defaultAudioEnabled: source?.defaultAudioEnabled ?? false,
        items: source === undefined ? [] : source.items.map((item) => ({ ...item })),
        totalDurationMs: source?.totalDurationMs ?? 0,
        createdBy: "usr_editor",
        createdAt: now,
        updatedAt: now,
        approvedAt: null,
        etag: '"1"',
      };
      playlistRevisions.push(created);
      return delay(created);
    },
    updatePlaylistRevision: (revisionId, input, etag) => {
      const index = playlistRevisions.findIndex((item) => item.id === revisionId);
      const current = playlistRevisions[index];
      if (current === undefined) {
        return Promise.reject(new ApiError(404, "NOT_FOUND", "プレイリストの版が見つかりません。", null));
      }
      if (current.state !== "draft") {
        return Promise.reject(new ApiError(409, "IMMUTABLE_REVISION", "公開済みの版は編集できません。", null));
      }
      if (current.etag !== etag) {
        return Promise.reject(new ApiError(409, "VERSION_CONFLICT", "他の担当者が先に更新しました。", null));
      }
      const version = Number(current.etag.replaceAll('"', "")) + 1;
      const updated: PlaylistRevision = {
        ...current,
        defaultAudioEnabled: input.defaultAudioEnabled,
        items: input.items.map((item) => ({ ...item })),
        totalDurationMs: input.items.reduce((total, item) => {
          const asset = media.find((candidate) => candidate.id === item.mediaAssetId);
          return total + (asset?.durationMs ?? 0) * item.repeatCount;
        }, 0),
        updatedAt: new Date().toISOString(),
        etag: JSON.stringify(String(version)),
      };
      playlistRevisions[index] = updated;
      return delay(updated);
    },
    validatePlaylistRevision: (revisionId) => {
      const revision = playlistRevisions.find((item) => item.id === revisionId);
      if (revision === undefined) {
        return Promise.reject(new ApiError(404, "NOT_FOUND", "プレイリストの版が見つかりません。", null));
      }
      const errors: ValidationResult["errors"][number][] = [];
      const warnings: ValidationResult["warnings"][number][] = [];
      if (revision.items.length === 0) {
        errors.push({ code: "PLAYLIST_EMPTY", message: "動画を1本以上追加してください。", field: "items" });
      }
      for (const item of revision.items) {
        const asset = media.find((candidate) => candidate.id === item.mediaAssetId);
        if (asset?.status !== "ready" || asset.durationMs === null) {
          errors.push({ code: "MEDIA_NOT_READY", message: "公開準備が完了していない動画があります。", resourceId: item.mediaAssetId });
        }
        const audioEnabled = item.audioEnabled ?? revision.defaultAudioEnabled;
        if (audioEnabled && asset?.hasAudio === false) {
          warnings.push({ code: "MEDIA_HAS_NO_AUDIO", message: "音声ONですが、音声トラックのない動画があります。", resourceId: item.mediaAssetId });
        }
      }
      return delay({ valid: errors.length === 0, errors, warnings });
    },
    listSchedules: () => delay(page(schedules)),
    createSchedule: () =>
      Promise.reject(
        new Error("スケジュール作成はfixtureでは実行できません。実APIへ接続してください。"),
      ),
    getSchedule: (id) => {
      const schedule = schedules.find((item) => item.id === id);
      if (schedule === undefined) {
        return Promise.reject(
          new ApiError(404, "NOT_FOUND", "スケジュールが見つかりません。", null),
        );
      }
      const revisions = scheduleRevisions.filter(
        (item) => item.scheduleId === id,
      );
      return delay<ScheduleDetail>({
        schedule,
        latestRevision: revisions.at(-1) ?? null,
        revisions,
      });
    },
    createScheduleRevision: () =>
      Promise.reject(
        new Error("Revision作成はfixtureでは実行できません。実APIへ接続してください。"),
      ),
    getScheduleRevision: (revisionId) => {
      const revision = scheduleRevisions.find((item) => item.id === revisionId);
      if (revision === undefined) {
        return Promise.reject(
          new ApiError(404, "NOT_FOUND", "Revisionが見つかりません。", null),
        );
      }
      return delay({
        revision,
        validation: { valid: true, errors: [], warnings: [] },
        conflictingRevisionIds: [],
      });
    },
    updateScheduleRevision: () =>
      Promise.reject(
        new Error("Revision更新はfixtureでは実行できません。実APIへ接続してください。"),
      ),
    validateScheduleRevision: () =>
      delay({ valid: true, errors: [], warnings: [] }),
    createPublicationRequest: () =>
      Promise.reject(
        new Error("承認依頼の作成はfixtureでは実行できません。実APIへ接続してください。"),
      ),
    submitPublicationRequest: () =>
      Promise.reject(
        new Error("申請はfixtureでは実行できません。実APIへ接続してください。"),
      ),
    publishPublicationRequest: () =>
      Promise.reject(
        new Error("公開はfixtureでは実行できません。実APIへ接続してください。"),
      ),
    createDevice: () =>
      Promise.reject(
        new Error("端末登録はfixtureでは実行できません。実APIへ接続してください。"),
      ),
    getCurrentUser: () =>
      delay({
        id: "usr_demo",
        email: "demo@example.invalid",
        displayName: "確認用利用者",
        organizationId: ORG,
        organizationName: "確認用組織",
        roles: ["admin", "approver", "editor", "viewer"],
        permissions: [
          "audit:read",
          "content:write",
          "device:manage",
          "display:manage",
          "publication:publish",
          "publication:request",
          "publication:review",
          "resource:read",
        ],
      }),
    listDisplays: () => delay(page(displays)),
    // 書き込みはfixtureで再現しない。画面確認は実APIで行う。
    createDisplay: () => Promise.reject(unavailable("LEDの作成")),
    getDisplay: (displayId) => {
      const found = displays.find((item) => item.display.id === displayId);
      return found === undefined
        ? Promise.reject(
            new ApiError(404, "NOT_FOUND", "LEDが見つかりません。", null),
          )
        : delay(found);
    },
    updateDisplay: () => Promise.reject(unavailable("LEDの更新")),
    archiveDisplay: () => Promise.reject(unavailable("LEDのアーカイブ")),
    revokeDevice: () => Promise.reject(unavailable("端末の退役")),
    rollbackDisplay: () => Promise.reject(unavailable("配信版の巻き戻し")),
    listPublicationRequests: () => delay(page(publicationRequests)),
    getPublicationRequest: (id) => {
      const request = publicationRequests.find((item) => item.id === id);
      if (request === undefined) {
        return Promise.reject(new Error("承認依頼が見つかりません。"));
      }
      return delay({
        request,
        changes: [],
        preparation: {
          state: request.validation.valid ? "ready" : "blocked",
          readyAssetCount: request.validation.valid ? 3 : 2,
          totalAssetCount: 3,
          targets: [],
          // 音声ありと消音の両方を出し、承認画面の警告を確認できるようにする。
          assets: media.slice(0, 2).map((item, index) => ({
            mediaAssetId: item.id,
            originalName: item.originalName,
            durationMs: item.durationMs,
            hasAudio: item.hasAudio,
            audioEnabled: index === 0 && item.hasAudio,
          })),
        },
      });
    },
    listAuditLogs: (query) => {
      const filtered = auditLogs.filter((item) =>
        (query?.actorType === undefined || item.actorType === query.actorType) &&
        (query?.outcome === undefined || item.outcome === query.outcome) &&
        (query?.action === undefined || item.action === query.action) &&
        (query?.targetType === undefined || item.targetType === query.targetType) &&
        (query?.targetId === undefined || item.targetId === query.targetId) &&
        (query?.displayId === undefined || item.displayId === query.displayId) &&
        (query?.from === undefined || item.occurredAt >= query.from) &&
        (query?.until === undefined || item.occurredAt <= query.until),
      );
      return delay(page(filtered.slice(0, query?.limit ?? 50)));
    },
    listPlaybackEvents: (query) => {
      const deviceIds =
        query?.displayId === undefined
          ? null
          : displays
              .filter((item) => item.display.id === query.displayId)
              .flatMap((item) =>
                item.device === null ? [] : [item.device.id],
              );
      const filtered = playbackEvents.filter(
        (item) =>
          (query?.deviceId === undefined || item.deviceId === query.deviceId) &&
          (deviceIds === null || deviceIds.includes(item.deviceId)) &&
          (query?.from === undefined || item.occurredAt >= query.from) &&
          (query?.until === undefined || item.occurredAt <= query.until),
      );
      return delay(page(filtered.slice(0, query?.limit ?? 50)));
    },
    approvePublicationRequest: (id) => {
      const request = publicationRequests.find((item) => item.id === id);
      if (request === undefined) {
        return Promise.reject(new ApiError(404, "NOT_FOUND", "承認依頼が見つかりません。", null));
      }
      if (request.status !== "submitted") {
        return Promise.reject(
          new ApiError(409, "INVALID_STATE_TRANSITION", "申請の状態が変わっています。", null),
        );
      }
      if (request.requestedBy === FIXTURE_ACTOR) {
        return Promise.reject(
          new ApiError(
            409,
            "SEPARATION_OF_DUTIES_REQUIRED",
            "申請者は自分の依頼を承認できません。",
            null,
          ),
        );
      }
      if (!request.validation.valid) {
        return Promise.reject(
          new ApiError(
            422,
            "PUBLICATION_VALIDATION_FAILED",
            "公開前検査でエラーが残っています。",
            null,
          ),
        );
      }
      const updated: PublicationRequest = {
        ...request,
        status: "approved",
        reviewedBy: FIXTURE_ACTOR,
        reviewedAt: new Date().toISOString(),
      };
      publicationRequests[publicationRequests.indexOf(request)] = updated;
      return delay(updated);
    },
    rejectPublicationRequest: (id, input) => {
      const request = publicationRequests.find((item) => item.id === id);
      if (request === undefined) {
        return Promise.reject(new ApiError(404, "NOT_FOUND", "承認依頼が見つかりません。", null));
      }
      if (request.status !== "submitted") {
        return Promise.reject(
          new ApiError(409, "INVALID_STATE_TRANSITION", "申請の状態が変わっています。", null),
        );
      }
      const updated: PublicationRequest = {
        ...request,
        status: "rejected",
        reviewedBy: FIXTURE_ACTOR,
        reviewedAt: new Date().toISOString(),
        reviewComment: input.reason,
      };
      publicationRequests[publicationRequests.indexOf(request)] = updated;
      return delay(updated);
    },
    getDeviceReleaseStatus: () => delay(deviceReleaseProgress()),
    getLatestScreenshot: () =>
      delay({
        screenshot: {
          id: "shot_fixture",
          deviceId: "dev_fixture",
          sourceActionId: null,
          contentType: "image/png",
          byteSize: 184_320,
          sha256: "c".repeat(64),
          capturedAt: "2026-08-29T03:58:00.000Z",
          receivedAt: "2026-08-29T03:58:12.000Z",
          expiresAt: "2026-09-05T03:58:12.000Z",
        },
        // 実物は署名付きURL。fixtureでも「短時間で切れる」前提を崩さない。
        downloadUrl: FIXTURE_SCREENSHOT,
        downloadUrlExpiresAt: "2026-08-29T04:05:00.000Z",
      }),
    requestScreenshot: (deviceId) =>
      delay({
        id: "act_fixture",
        deviceId,
        status: "pending",
        requestedBy: FIXTURE_ACTOR,
        requestedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
        deliveredAt: null,
        completedAt: null,
      }),
  };
}
